<template>
  <div class="swipe-list">
    <transition-group name="swipe-fade" tag="div" @before-leave="beforeLeave" @after-leave="afterLeave">
      <div
        v-for="listItem in items"
        :key="listItem.id"
        class="swipe-row"
        :data-item-id="listItem.id"
        @mousedown="startSwipe($event, listItem)"
        @mousemove="moveSwipe($event, listItem)"
        @mouseup="endSwipe($event, listItem)"
        @mouseleave="cancelSwipe(listItem)"
        @touchstart="startSwipe($event, listItem)"
        @touchmove="moveSwipe($event, listItem)"
        @touchend="endSwipe($event, listItem)"
        @touchcancel="cancelSwipe(listItem)"
      >
        <!-- Actions -->
        <div class="swipe-actions-container" :style="mailStyleSwipe ? { backgroundColor: actions[0]?.backgroundColor || 'var(--background-color-alt)' } : {}" @mousedown.stop @touchstart.stop>
          <div class="swipe-actions swipe-actions-right" :style="Object.assign({}, getActionsStyle(listItem), { width: getActionsWidth(listItem) })" @mousedown.stop @touchstart.stop>
            <template v-if="getVisibleActions(listItem).length > 0">
              <div 
                v-for="action in getVisibleActions(listItem)" 
                :key="action.type"
                class="action-container"
                :style="{
                  backgroundColor: action.backgroundColor || 'var(--background-color-alt)',
                  width: '100%'
                }"
              >
                <img
                  v-if="action.image"
                  :src="action.image"
                  class="action-img"
                  @mousedown.stop
                  @touchstart.stop
                  @click.stop.prevent="$emit('action-click', { item: listItem, action })"
                />
                <v-btn
                  v-else
                  :icon="action.icon"
                  variant="text"
                  size="large"
                  :color="action.color || 'grey'"
                  class="action-btn"
                  @mousedown.stop
                  @touchstart.stop
                  @click.stop.prevent="$emit('action-click', { item: listItem, action })"
                ></v-btn>
              </div>
            </template>
            <slot name="actions" :item="listItem" v-else></slot>
          </div>
        </div>

        <!-- Main content -->
        <div 
          class="swipe-content"
          :style="getContentStyle(listItem)"
          @click.stop="handleContentClick(listItem)"
        >
          <slot :item="listItem"></slot>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
// Global state to track currently open swipe
const globalSwipeState = {
  currentOpenId: null,
  setOpenId(id) {
    if (this.currentOpenId !== id) {
      const oldId = this.currentOpenId;
      this.currentOpenId = id;
      return oldId; // Return the previous open ID so we can close it
    }
    return null;
  },
  clearOpenId() {
    this.currentOpenId = null;
  }
};

export default {
  name: 'SwipeActionsContainer',
  props: {
    preview: {
      type: Boolean,
      default: true
    },
    items: {
      type: Array,
      required: true
    },
    swipeThreshold: {
      type: Number,
      default: 80
    },
    actionWidth: {
      type: Number,
      default: 120
    },
    backgroundColor: {
      type: String,
      default: 'var(--background-color)'
    },
    actions: {
      type: Array,
      default: () => [
        {
          icon: 'mdi-pencil',
          color: 'grey',
          type: 'edit',
          backgroundColor: 'var(--background-color-alt)' // Default background color
        },
        {
          icon: 'mdi-delete',
          color: 'grey',
          type: 'delete',
          backgroundColor: 'var(--background-color-alt)' // Default background color
        }
      ]
    },
    mailStyleSwipe: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      swipeStates: new Map(), // Map to store swipe state for each item
      clickStartTime: null,    // Track when a click started
      clickStartX: null,       // Track where a click started
      clickThreshold: 10,      // Maximum pixels to move for a click
      clickTimeThreshold: 200,  // Maximum time for a click in ms
      pendingDeleteIds: [],     // Track items pending delete
      leavingIds: [] // Track ids of rows being removed
    }
  },
  watch: {
    items: {
      handler(newItems) {
        // Initialize or update swipe states for all items
        newItems.forEach(item => {
          if (!this.swipeStates.has(item.id)) {
            this.swipeStates.set(item.id, {
              startX: 0,
              currentX: 0,
              swiping: false,
              initialOffset: 0,
              finalOffset: 0,
              isSwiping: false
            });
          }
        });
        // Clean up swipe states for removed items
        this.swipeStates.forEach((state, id) => {
          if (!newItems.find(item => item.id === id)) {
            this.swipeStates.delete(id);
          }
        });
      },
      immediate: true
    }
  },
  mounted() {
    // Animate the first item to briefly show swipe action
    if (this.items.length > 0) {
      const firstItem = this.items[0];
      const swipeState = this.getSwipeState(firstItem);
      if (swipeState && this.preview) {
        // Set initial state with a slight delay to ensure smooth animation
        setTimeout(() => {
          Object.assign(swipeState, {
            finalOffset: -this.actionWidth
          });
          
          // After showing the action, wait longer before closing
          setTimeout(() => {
            Object.assign(swipeState, {
              finalOffset: 0
            });
          }, 500); // Show for .5 seconds
        }, 500); // Wait 500ms before starting the animation
      }
    }
  },
  methods: {
    getSwipeState(item) {
      return this.swipeStates.get(item.id);
    },
    startSwipe(event, item) {
      const swipeState = this.getSwipeState(item);
      if (!swipeState) return;

      // Prevent default touch behavior only if horizontal swipe is detected later
      // Record click start time and position
      this.clickStartTime = Date.now();
      this.clickStartX = event.type === 'mousedown' ? event.clientX : event.touches[0].clientX;
      swipeState.startY = event.type === 'mousedown' ? event.clientY : event.touches[0].clientY;

      // Check if there's another item open and close it
      const previousOpenId = globalSwipeState.setOpenId(item.id);
      if (previousOpenId && previousOpenId !== item.id) {
        const previousItem = this.items.find(i => i.id === previousOpenId);
        if (previousItem) {
          this.resetSwipeState(previousItem);
        }
      }

      const clientX = event.type === 'mousedown' ? event.clientX : event.touches[0].clientX;

      // Only allow swiping left (negative offset)
      if (swipeState.finalOffset) {
        // If already swiped, only allow swiping back to 0
        Object.assign(swipeState, {
          startX: clientX,
          currentX: clientX,
          swiping: true,
          initialOffset: swipeState.finalOffset,
          isSwiping: false
        });
      } else {
        Object.assign(swipeState, {
          startX: clientX,
          currentX: clientX,
          swiping: true,
          initialOffset: 0,
          finalOffset: 0,
          isSwiping: false
        });
      }

      // Emit swipe start event
      this.$emit('swipe-start', { item, event });
    },

    resetSwipeState(item) {
      const swipeState = this.getSwipeState(item);
      if (!swipeState) return;

      // Reset all swipe states
      Object.assign(swipeState, {
        swiping: false,
        isSwiping: false,
        startX: 0,
        currentX: 0,
        initialOffset: 0,
        finalOffset: 0
      });

      // Force visual reset
      this.$nextTick(() => {
        const content = this.$el.querySelector(`[data-item-id="${item.id}"] .swipe-content`);
        if (content) {
          content.style.transition = 'transform 0.3s ease';
          content.style.transform = 'translateX(0)';
        }
      });
    },

    moveSwipe(event, item) {
      const swipeState = this.getSwipeState(item);
      if (!swipeState || !swipeState.swiping) return;
      
      const clientX = event.type === 'mousemove' ? event.clientX : event.touches[0].clientX;
      const clientY = event.type === 'mousemove' ? event.clientY : event.touches[0].clientY;
      const diffX = clientX - swipeState.startX;
      const diffY = clientY - (swipeState.startY || clientY);

      // Only prevent default if horizontal movement is greater than vertical
      if (event.type === 'touchmove' && Math.abs(diffX) > Math.abs(diffY)) {
        event.preventDefault();
      }
      
      // If we've moved more than the click threshold, it's definitely a swipe
      if (Math.abs(diffX) > this.clickThreshold) {
        swipeState.isSwiping = true;
      }
      
      // Calculate the new offset, limiting it to the actionWidth
      let newOffset = (swipeState.initialOffset || 0) + diffX;
      if (newOffset > 0) {
        newOffset = 0;
      }
      if (!this.mailStyleSwipe && newOffset < -this.actionWidth) {
        newOffset = -this.actionWidth;
      }
      // If mailStyleSwipe is true, allow swiping further left
      swipeState.currentX = swipeState.startX + (newOffset - (swipeState.initialOffset || 0));

      // --- Apple Mail style: auto-delete if swiped past -85% of row width ---
      if (this.mailStyleSwipe) {
        let rowWidth = 1;
        if (this.$el && this.$el.querySelector) {
          const row = this.$el.querySelector(`[data-item-id="${item.id}"]`);
          if (row && row.offsetWidth) {
            rowWidth = row.offsetWidth;
          }
        }
        if (newOffset < -0.80 * rowWidth && !this.pendingDeleteIds.includes(item.id)) {
          const deleteAction = this.actions.find(a => a.type === 'delete');
          if (deleteAction) {
            this.pendingDeleteIds.push(item.id);
            // Animate to fully open
            if (swipeState) {
              swipeState.finalOffset = -rowWidth;
              swipeState.swiping = false;
              swipeState.isSwiping = false;
            }
            // Wait for the animation to finish before emitting delete
            setTimeout(() => {
              this.$emit('action-click', { item, action: deleteAction });
            }, 300); // match the transition duration in getContentStyle
            return;
          }
        }
      }

      // Emit swipe move event
      this.$emit('swipe-move', { item, event, offset: this.getSwipeOffset(item) });
    },

    endSwipe(event, item) {
      const swipeState = this.getSwipeState(item);
      if (!swipeState || !swipeState.swiping) return;

      const clientX = event.type === 'mouseup' ? event.clientX : event.changedTouches[0].clientX;
      const diff = clientX - swipeState.startX;
      const timeDiff = Date.now() - this.clickStartTime;
      
      // Check if this was a click (short time and small movement)
      const isClick = Math.abs(diff) <= this.clickThreshold && timeDiff <= this.clickTimeThreshold;
      
      if (isClick) {
        // Reset swipe state and emit click
        this.resetSwipeState(item);
        this.$emit('content-click', { item });
        return;
      }

      const totalOffset = (swipeState.initialOffset || 0) + diff;
      
      let finalOffset = 0;
      
      // --- Apple Mail style: snap and delete on release ---
      if (this.mailStyleSwipe) {
        let rowWidth = 1;
        if (this.$el && this.$el.querySelector) {
          const row = this.$el.querySelector(`[data-item-id="${item.id}"]`);
          if (row && row.offsetWidth) {
            rowWidth = row.offsetWidth;
          }
        }
        // If swiped past -80% of row width, snap fully open and trigger delete
        if (totalOffset <= -0.9 * rowWidth) {
          finalOffset = -rowWidth;
          if (!this.pendingDeleteIds.includes(item.id)) {
            this.pendingDeleteIds.push(item.id);
            const deleteAction = this.actions.find(a => a.type === 'delete');
            if (deleteAction) {
              this.$emit('action-click', { item, action: deleteAction });
            }
          }
        } else if (totalOffset <= -this.swipeThreshold) {
          finalOffset = -this.actionWidth;
        } else {
          finalOffset = 0;
          globalSwipeState.clearOpenId();
        }
        // Update state with final position
        Object.assign(swipeState, {
          swiping: false,
          finalOffset: finalOffset,
          isSwiping: false
        });
        this.clickStartTime = null;
        this.clickStartX = null;
        this.$emit('swipe-end', { item, event, offset: finalOffset });
        return;
      }

      if (swipeState.initialOffset < 0) {
        // If we started swiped left, allow swiping back to 0
        if (totalOffset > -this.swipeThreshold) {
          finalOffset = 0;
          globalSwipeState.clearOpenId(); // Clear the open ID if we're closing
        } else {
          finalOffset = -this.actionWidth;
        }
      } else {
        // From original position, only allow left swipe
        if (totalOffset <= -this.swipeThreshold) {
          finalOffset = -this.actionWidth;
        } else {
          globalSwipeState.clearOpenId(); // Clear the open ID if we're not swiping far enough
        }
      }
      
      // Update state with final position
      Object.assign(swipeState, {
        swiping: false,
        finalOffset: finalOffset,
        isSwiping: false
      });

      // Reset click tracking
      this.clickStartTime = null;
      this.clickStartX = null;

      // Emit swipe end event
      this.$emit('swipe-end', { item, event, offset: finalOffset });
    },

    cancelSwipe(item) {
      this.resetSwipeState(item);
      globalSwipeState.clearOpenId();
      this.$emit('swipe-cancel', { item });
    },

    getSwipeOffset(item) {
      const swipeState = this.getSwipeState(item);
      if (!swipeState) return 0;

      if (swipeState.swiping) {
        const diff = swipeState.currentX - swipeState.startX;
        return (swipeState.initialOffset || 0) + diff;
      }
      
      return swipeState.finalOffset || 0;
    },

    getContentStyle(item) {
      // If pending delete or leaving, force fully open
      if (this.pendingDeleteIds.includes(item.id) || this.leavingIds.includes(item.id)) {
        return {
          transform: `translateX(-100%)`,
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        };
      }
      const offset = this.getSwipeOffset(item);
      const swipeState = this.getSwipeState(item);
      return {
        transform: `translateX(${offset}px)`,
        transition: swipeState?.swiping ? 'none' : 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: offset < 0 ? '0 8px 8px 0' : '0'
      };
    },

    getActionsStyle(item) {
      const swipeState = this.getSwipeState(item);
      return {
        transition: swipeState?.swiping ? 'none' : 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
      };
    },

    handleContentClick(item) {
      // This is now just a fallback for direct clicks
      // Most clicks will be handled in endSwipe
      this.$emit('content-click', { item });
    },

    getVisibleActions(item) {
      if (!this.mailStyleSwipe) return this.actions;
      // If pending delete, only show delete
      if (this.pendingDeleteIds.includes(item.id)) {
        return this.actions.filter(a => a.type === 'delete');
      }
      const offset = this.getSwipeOffset(item);
      let rowWidth = 1;
      if (this.$el && this.$el.querySelector) {
        const row = this.$el.querySelector(`[data-item-id="${item.id}"]`);
        if (row && row.offsetWidth) {
          rowWidth = row.offsetWidth;
        }
      }
      if (offset < -0.6 * rowWidth) {
        const deleteAction = this.actions.find(a => a.type === 'delete');
        if (deleteAction) {
          let grow = Math.min(1, Math.max(0, (-offset - 0.6 * rowWidth) / (0.25 * rowWidth)));
          let widthPercent = 100 + grow * 100;
          return [{ ...deleteAction, _growWidth: widthPercent }];
        }
      }
      return this.actions;
    },

    getActionsWidth(item) {
      if (!this.mailStyleSwipe) return `${this.actionWidth}px`;
      // If leaving, force fully expanded
      if (this.leavingIds.includes(item.id)) {
        let rowWidth = 1;
        if (this.$el && this.$el.querySelector) {
          const row = this.$el.querySelector(`[data-item-id="${item.id}"]`);
          if (row && row.offsetWidth) {
            rowWidth = row.offsetWidth;
          }
        }
        return `${this.actionWidth + rowWidth}px`;
      }
      const offset = this.getSwipeOffset(item);
      let rowWidth = 1;
      if (this.$el && this.$el.querySelector) {
        const row = this.$el.querySelector(`[data-item-id="${item.id}"]`);
        if (row && row.offsetWidth) {
          rowWidth = row.offsetWidth;
        }
      }
      if (offset < -0.6 * rowWidth) {
        let grow = Math.min(1, Math.max(0, (-offset - 0.6 * rowWidth) / (0.25 * rowWidth)));
        let width = this.actionWidth + grow * rowWidth;
        return `${width}px`;
      }
      return `${this.actionWidth}px`;
    },

    // Call this from parent after animation to remove item
    removePendingDelete(id) {
      const idx = this.pendingDeleteIds.indexOf(id);
      if (idx !== -1) this.pendingDeleteIds.splice(idx, 1);
    },

    // For transition-group
    beforeLeave(el) {
      const id = el.getAttribute('data-item-id');
      if (id && !this.leavingIds.includes(id)) {
        this.leavingIds.push(id);
      }
    },
    afterLeave(el) {
      const id = el.getAttribute('data-item-id');
      const idx = this.leavingIds.indexOf(id);
      if (idx !== -1) this.leavingIds.splice(idx, 1);
    }
  }
}
</script>

<style scoped>
.swipe-list {
  margin-top: -10px;
  margin-bottom: -20px;
  width: 100%;
}

.swipe-row {
  position: relative;
  overflow: hidden;
  touch-action: pan-y pinch-zoom;
  min-height: 73px;
  border-bottom: 1px solid var(--border-color);
  background: v-bind(backgroundColor);
}

.swipe-row:last-child {
  border-bottom: none;
}

.swipe-content {
  position: relative;
  z-index: 2;
  cursor: pointer;
  user-select: none;
  width: 100%;
  height: 100%;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  background: v-bind(backgroundColor);
  overflow: hidden;
}

.swipe-actions-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
  height: 100%;
  overflow: hidden;
}

.swipe-actions {
  position: absolute;
  top: 1px;
  right: 0;
  height: 98%;
  width: 120px;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  pointer-events: auto;
  z-index: 1;
}

.action-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.swipe-actions :deep(.action-btn) {
  width: 40px !important;
  height: 40px !important;
  border-radius: 50%;
}

.action-btn {
  position: relative;
  z-index: 4;
}

.action-img {
  width: 24px;
  height: 24px;
  position: relative;
  z-index: 4;
}

.swipe-fade-enter-active, .swipe-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.swipe-fade-enter-from, .swipe-fade-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
.swipe-fade-leave-active {
  width: 100%;
}

/* Keep delete action fully expanded during leave */
.swipe-fade-leave-active .swipe-actions {
  width: 100% !important;
}
.swipe-fade-leave-active .action-container {
  width: 100% !important;
}
</style> 